import type { Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part, stack } from '../body';
import type { Worn } from '../headwear';
import { EYES, FACE } from '../looks';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, fromPath, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Pirate set (ADR-0160 amended, mythic): a pirate captain with a parrot on his shoulder. A black tricorn with a
 * captain's coat and the Blunderbuss.
 *
 * The head is the tricorn and what grows under it, over the wearer's own face (it shows through, in their skin, the
 * near eye theirs): a black felt tricorn, its upturned brim edged in gold, a white skull and crossbones on its front, a
 * plume in the team's colour curling back off its crown; under it shaggy dark hair at the back, a gold hoop in the ear,
 * a black eyepatch on its strap over the far eye, a scowling brow, a scruffy beard and moustache round a grin with a
 * gold tooth. Its flair: the parrot (red, a yellow shoulder, green wings, a long red tail) perched on the back shoulder,
 * bobbing, ruffling and flapping now and then (the bright moving part at 1:1), a glint on the hoop.
 * The suit is a long navy-black captain's coat: gold-edged lapels open on a white shirt, gold buttons, gold cuffs, a
 * sash in the team's colour across it, a wide black belt with a big gold buckle; black boots. Its flair: a coin glinting
 * on the buttons, gold coins dropped on the run.
 *
 * Rubric (`assetc mythic pirate` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside
 * Barnstormer): head S4 R5 P4 L4 C4 A4 F4, coat S4 R4 P4 L4 C4 F4. Weakest: the coat's navy goes dark at night; its gold
 * and the parrot carry it there.
 */

// --- The tricorn and the captain under it -----------------------------------------------------------------------------

/** The crown of the hat over the brim (u forward, v down from the head's middle). */
const CROWN: Ellipse = [-1, -14, 14, 9.5];
/** Half the brim's width, round its middle. */
const BRIM_W = 24.5;
const BRIM_U = -1;
/** The brim's upturned top edge: low over the brow's middle, rising to two horns at its sides. */
const brimTop = (u: number) => -11 - 8.5 * (Math.abs(u - BRIM_U) / BRIM_W) ** 1.7;
/** Its lower edge: down over the brow at its middle, up at the horns. */
const brimBottom = (u: number) =>
  -1 - 3.5 * (Math.abs(u - BRIM_U) / BRIM_W) ** 2 - Math.max(0, Math.abs(u - BRIM_U) - 19) * 2.4;
/** The skull on its front: its middle. */
const SKULL = [-1, -5.6] as const;
/** The plume: a path curling back off the crown, its tip drooping. */
const PLUME = [-6, -20, -13, -22, -20, -21.5, -26, -19, -28.5, -14] as const;
/** The beard round the jaw. */
const BEARD: Ellipse = [4.5, 11.5, 13.5, 7.2];
/** The eyepatch over the far eye. */
const PATCH: Ellipse = [11.8, 8, 2.9, 3.3];

/** A little hash for scruff (the same pixel, the same answer). */
const hash = (a: number, b: number) => (((Math.floor(a) * 73856093) ^ (Math.floor(b) * 19349663)) >>> 0) % 7;

const brim = (u: number, v: number, lx: number, ly: number): PaletteKey | null => {
  const d = Math.abs(u - BRIM_U);
  if (d > BRIM_W) return null;
  const [top, bottom] = [brimTop(u), brimBottom(u)];
  if (v < top || v > bottom) return null;
  // Its gold edge along the top, lit on the left.
  if (v - top < 1.9) return v - top < 0.9 ? (u < BRIM_U + 6 ? 'y' : 'Y') : 'U';
  // The skull and crossbones on its front.
  const [sx, sy] = [u - SKULL[0], v - SKULL[1]];
  const skull = Math.hypot(sx / 3.3, (sy + 0.6) / 2.9);
  if (skull < 1) {
    if (Math.abs(sy + 0.3) < 0.8 && (Math.abs(sx + 1.4) < 0.8 || Math.abs(sx - 1.4) < 0.8)) return 'k';
    return sx > 1.6 && sy > 0 ? '5' : 'w';
  }
  if (sy > 1.5 && sy < 3.4 && Math.abs(sx) < 1.9) return Math.floor(sx + 10) % 2 && sy > 2.4 ? '5' : 'w';
  for (const s of [1, -1])
    if (Math.abs(sx - s * sy) < 0.75 && Math.abs(sx) < 3.9 && Math.abs(sy) < 3.9) return s * sx > 0 ? 'w' : '5';
  // Black felt: lit to the top-left, a fold where the front corner turns.
  if (bottom - v < 1.2) return 'k';
  if (Math.abs(u - BRIM_U) < 0.6 && v < SKULL[1] - 3.5) return '2';
  const l = lx * (u - BRIM_U) * -0.04 + ly * (v - top) * 0.12;
  return d > BRIM_W - 2.5 ? '2' : l > 0.25 ? '2' : '1';
};

const tricorn: HeadPicture = (u, v, lx, ly, lying) => {
  const b = brim(u, v, lx, ly);
  if (b) return b;
  // The crown behind the brim.
  const crown = inEllipse(u, v, CROWN);
  if (crown <= 1 && v < brimTop(u) + 1) {
    const l = domeLight(u, v, CROWN, lx, ly);
    if (crown > 0.8) return l > 0.6 ? '2' : 'k';
    return l > 0.93 ? '3' : l > 0.5 ? '2' : '1';
  }
  // The plume curling back off it: the team's colour, its quill, its barbs.
  const p = fromPath(u, v, PLUME);
  const along = Math.hypot(u - PLUME[0], v - PLUME[1]);
  const w = 3.2 - along * 0.06;
  if (p < w && v < -14) {
    if (p < 0.6) return 'c';
    if ((Math.floor(u) + Math.floor(v)) % 3 === 0) return 'B';
    return v < -23 || u > -12 ? 'c' : 'b';
  }
  // Under the brim: the captain.
  if (v < brimBottom(u)) return null;
  // A gold hoop in the ear.
  const hoop = Math.hypot(u + 12.5, v - 12.5);
  if (hoop > 0.9 && hoop < 2.1 && v > 11) return u < -12.5 ? 'y' : 'Y';
  // Shaggy dark hair at the back, falling to the collar.
  if (u < -13.5 && v < 15 + (hash(u, 3) % 3) && Math.hypot(u + 2, v - 3) < 19.5) {
    // Locks in streaks: a lit one every third column up top, the shade under the brim and down its edge.
    const col = Math.floor(u + 30) % 3;
    if (v < 1.5 || u > -14.5 || v > 13) return 'N';
    return col === 0 && v < 9 ? 'm' : 'n';
  }
  // The eyepatch on its strap.
  const patch = inEllipse(u, v, PATCH);
  if (patch <= 1) return patch < 0.35 && u < PATCH[0] && v < PATCH[1] ? '2' : 'k';
  if (Math.abs(v - (PATCH[1] - 3) - (u - 9.5) * 0.85) < 0.6 && u > 2.5 && u < 10) return 'k';
  if (Math.abs(v - 6.5 + (u - 14) * 0.25) < 0.6 && u > 14 && u < 18.5) return 'k';
  // A scowling brow over the near eye.
  if (!lying && Math.abs(v - 3.2 - (u + 3) * 0.28) < 0.55 && u > -3.5 && u < 2.5) return 'N';
  // The moustache, drooping at its ends, and the grin under it with a gold tooth.
  if (u > 2 && u < 13.5) {
    const droop = Math.max(0, Math.abs(u - 7.7) - 3.5) * 0.7;
    if (v > 10.2 + droop * 0.4 && v < 12 + droop) return u < 6 && v < 11.2 ? 'm' : 'n';
    if (v >= 12 && v < 14 && u > 4.5 && u < 10.5)
      return v < 13 ? 'k' : Math.floor(u) === 8 ? 'Y' : Math.floor(u) === 5 || Math.floor(u) === 10 ? 'k' : 'w';
  }
  // The scruffy beard round the jaw and a sideburn up to the ear.
  const beard = inEllipse(u, v, BEARD);
  const scruff = hash(u, v) / 14;
  if ((beard <= 1 - scruff && v > 11.8) || (u > -11 && u < -7.5 && v > 6 && v < 14)) {
    // Hair in streaks: lit ones down its lit side, dark ones in its shade.
    const col = Math.floor(u + 30) % 4;
    if (u > 13 || v > 16.5) return 'N';
    if (col === 0 && u < 9 && v < 15.5) return 'm';
    return col === 2 && v > 14 ? 'N' : 'n';
  }
  return null;
};

/** The mask, pictured alone too (the wardrobe's tile): over the default face, the near eye showing. */
const TRICORN = (): Worn => {
  const worn = mask(tricorn);
  return { ...worn, alone: stack(FACE, EYES.round.open, worn.front) };
};

// --- The captain's coat ----------------------------------------------------------------------------------------------

/** The coat standing (the frame's x 5-56, y 39-57): navy-black, gold-edged lapels, a white shirt, a sash, a belt. */
function coat(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    // The skirts flare a little at the hem.
    if (x >= 16 - Math.max(0, y - 53) * 0.5 && x <= 43 + Math.max(0, y - 53) * 0.5) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  const arm = (x: number) => x < 16 || x > 43;
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const shade = !inSuit(x + 1, y) || (x > 41 && !arm(x));
      // Gold cuffs at the sleeves' ends.
      if (arm(x) && y >= 50 && y <= 52) {
        at(x, y, y === 50 ? 'y' : x > 47 ? 'U' : 'Y');
        continue;
      }
      // The hem: a gold edge.
      if (y === 57 && !arm(x)) {
        at(x, y, x > 41 ? 'U' : 'Y');
        continue;
      }
      // Folds in the coat.
      const fold = !arm(x) && (x === 20 || x === 39) && y > 47;
      at(x, y, lit ? '3' : shade ? '1' : fold ? '1' : '2');
    }
  // The shirt in the open front: a white V with a ruffle of jabot, the lapels edged in gold either side.
  for (let y = 43; y <= 53; y++) {
    const half = Math.max(0, 4.5 - (y - 43) * 0.42);
    for (let x = 24; x <= 36; x++) {
      const d = x + 0.5 - 30;
      if (Math.abs(d) < half) at(x, y, (x + y) % 4 === 0 ? '5' : x > 31 ? '5' : 'w');
      else if (Math.abs(d) < half + 1.2) at(x, y, d < 0 ? 'y' : 'Y');
      else if (Math.abs(d) < half + 3 && y < 50) at(x, y, d < 0 ? '3' : '1');
    }
  }
  // The sash in the team's colour, across the coat from the near shoulder to the far hip.
  for (let y = 42; y <= 56; y++)
    for (let x = 16; x <= 43; x++) {
      const t = (x - 17) * 0.45 + 42 - y;
      if (Math.abs(t) < 1.7 && y > 43) at(x, y, t > 0.6 ? 'c' : t < -0.6 ? 'B' : 'b');
    }
  // Gold buttons down either side of the front.
  for (const [x, y] of [
    [22, 46],
    [22, 49],
    [37, 46],
    [37, 49],
  ] as const) {
    at(x, y, 'y');
    at(x + 1, y, 'Y');
    at(x, y + 1, 'Y');
    at(x + 1, y + 1, 'U');
  }
  // The belt and its big gold buckle.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, '1');
    at(x, 54, 'k');
    at(x, 55, x > 40 ? 'k' : '1');
  }
  c.stamp(
    `
    YYYYYYY
    Yy.k.UY
    Yy.k.UY
    UUUUUUU
  `,
    27 - x0,
    52 - y0,
  );
  return [c, x0, y0];
}

/** The coat lying (right of the legs, under the head): the belt and buckle (left), the sash, the shirt by the head. */
const DEAD_COAT = grid(`
  ...33222
  ..332222
  1Y2bc2ww
  kY2bb2w5
  1UYYY2ww
  kUy.Yy5w
  1UYYYyww
  kY2b2y5w
  1Y22by2w
  kY222byw
  1Y2222by
  kY2222yb
  1Y2y222b
  kY2222Yc
  1Y22223b
  kY222232
  kU111111
  ....1111
`);

/** The team's trousers, black boots. */
const COAT = (): Outfit => ({ torso: coat(), dead: [DEAD_COAT, 24, 51], legs: { n: '1', N: 'k', m: '2' } });

// --- The Blunderbuss's brass and walnut on every other gun -----------------------------------------------------------

/** The pirate's finish (the default gun is its own, `pollinator_pirate`): brass for steel, its wood kept. */
const BRASS = { '3': 'U', '4': 'Y', '5': 'y' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const SILVER = 0xc0cbdc;
const GREY = 0x8b9bb4;
const YELLOW = 0xfee761;
const GOLD = 0xfeae34;
const ORANGE = 0xf77622;

/**
 * The parrot (24 × 30, its feet at (11, 26)), facing forward: a red body lit pink, a white face round its eye, a pale
 * hooked beak, a yellow shoulder on green wings, a long red tail, gold feet; drawn as a picture of its wing at `wing`
 * radians (folded down its back at 0, raised over it at 1.4, high at 2.2), its head bobbed `bob` px, its feathers
 * `ruffled`.
 */
/** The parrot is drawn this much bigger than its shapes (they were made at 19 × 24). */
const SCALE = 1.25;

function parrot(wing: number, bob: number, ruffled: boolean): string {
  const [W, H] = [24, 30];
  const out: string[][] = Array.from({ length: H }, () => Array(W).fill('.') as string[]);
  const body = (x: number, y: number): string | null => {
    const [px, py] = [(x + 0.5) / SCALE, (y + 0.5) / SCALE];
    // The wing: an ellipse from its shoulder, turned from down its back up over it.
    const [sx, sy] = [8, 11 + bob * 0.5];
    const a = Math.PI * 0.62 + wing;
    const len = wing > 0 ? 7.5 + wing * 0.5 : 8;
    const [ax, ay] = [Math.cos(a), Math.sin(a)];
    const [dx, dy] = [px - sx, py - sy];
    const t = dx * ax + dy * ay;
    const n = -dx * ay + dy * ax;
    if (t > -1 && t < len && Math.abs(n) < 2.6 * (1 - (t / len) ** 2 * 0.6)) {
      const f = t / len;
      // Its feathers' tips separate.
      if (f > 0.85 && Math.floor(n + 10) % 2 === 0 && wing > 0.5) return null;
      return f < 0.28 ? (n < 0 ? 'y' : 'Y') : f < 0.75 ? (n < -0.8 ? 'l' : 'g') : 'G';
    }
    // The head, the white face round its eye, the eye, the hooked beak.
    const [hx, hy] = [11, 7.5 + bob];
    const head = Math.hypot(px - hx, py - hy);
    if (px > 13.2 && px < 16.6 && py > hy - 1 && py < hy + 3.6) {
      const bx = px - 13.2;
      const by = py - hy + 1;
      if (by < 1.6 - bx * 0.15 || (bx > 2.4 && by < 3.2 + (bx - 2.4)) || bx < 1.1) return by > 2.6 ? '2' : 'e';
      if (by < 3.8 - bx * 0.5) return '2';
    }
    if (head < 3.7) {
      if (Math.hypot(px - 12.3, py - hy) < 0.8) return 'k';
      if (Math.hypot(px - 12.4, py - hy) < 1.9) return 'w';
      return px < hx - 1 && py < hy ? 'i' : py > hy + 2 ? 'R' : 'r';
    }
    // The body, leaning back a little; puffed out when ruffled.
    const [bx, by] = [9 - (py - 14) * 0.15, 14 + bob * 0.5];
    const r = ruffled ? 1.15 : 1;
    const e = Math.hypot((px - bx) / (4.3 * r), (py - by) / (6 * r));
    if (e < 1 && py > 9) {
      if (ruffled && e > 0.82 && Math.floor(py) % 2 === 0) return null;
      return px < bx - 1.5 && py < by ? 'i' : px > bx + 2 || py > by + 3.5 ? 'R' : 'r';
    }
    // The long tail hanging down behind the shoulder.
    const tail = Math.abs(px - (7 - (py - 18) * 0.9));
    if (py > 17 && py < 24 && tail < 1.4 - (py - 18) * 0.12) return py > 21 ? 'R' : 'r';
    // Gold feet gripping.
    if (py > 20 && py < 22 && (Math.abs(px - 8.5) < 0.8 || Math.abs(px - 10.8) < 0.8)) return 'Y';
    return null;
  };
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) (out[y] as string[])[x] = body(x, y) ?? '.';
  // Its outline: every empty pixel beside one drawn.
  const filled = (x: number, y: number) => out[y]?.[x] !== undefined && out[y]?.[x] !== '.' && out[y]?.[x] !== 'k';
  const rim: [number, number][] = [];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++)
      if (
        (out[y] as string[])[x] === '.' &&
        (filled(x - 1, y) || filled(x + 1, y) || filled(x, y - 1) || filled(x, y + 1))
      )
        rim.push([x, y]);
  for (const [x, y] of rim) (out[y] as string[])[x] = 'k';
  return out.map((r) => r.join('')).join('\n');
}

const PERCHED = parrot(0, 0, false);
const BOB = parrot(0, 1, false);
const RUFFLE = parrot(0, 0, true);
const HALF = parrot(1.4, 0, false);
const HIGH = parrot(2.2, -1, false);

/** Perched, a bob now and then, a ruffle, a flap of its wings. */
const PARROT: Glyph = {
  kind: 'glyph',
  // Its feet on the back shoulder's top (the frame's (12, 42)).
  at: { on: 'torso', x: 12 - 11, y: 42 - 26 },
  frames: [
    PERCHED,
    PERCHED,
    PERCHED,
    BOB,
    PERCHED,
    PERCHED,
    BOB,
    BOB,
    PERCHED,
    PERCHED,
    HALF,
    HIGH,
    HALF,
    HIGH,
    HALF,
    PERCHED,
    PERCHED,
    PERCHED,
    RUFFLE,
    PERCHED,
    RUFFLE,
    PERCHED,
    PERCHED,
    BOB,
  ],
  tones: {
    k: 0x181425,
    r: 0xe43b44,
    R: 0xa22633,
    i: 0xf6757a,
    w: WHITE,
    e: 0xead4aa,
    '2': 0x3a4466,
    y: YELLOW,
    Y: GOLD,
    l: 0x63c74d,
    g: 0x3e8948,
    G: 0x265c42,
  },
  hold: [0.1, 0.22],
  order: 'cycle',
  layer: 'front',
};

const FLAIR: SetFlair = {
  head: [
    PARROT,
    // A glint on the hoop.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 17.5, y: 41 },
      box: [2, 2],
      n: 1,
      period: [0.8, 1.4],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
  ],
  outfit: [
    // A glint running over the buttons and the buckle.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 30, y: 50 },
      box: [16, 8],
      n: 1,
      period: [0.5, 0.9],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
    // A gold coin dropped now and then on the run, bouncing off behind.
    {
      kind: 'emit',
      at: { on: 'torso', x: 22, y: 54 },
      box: [6, 2],
      rate: 0,
      run: 0.012,
      v: [-14, -4, -30, -18],
      g: 160,
      drag: 0.6,
      life: [0.35, 0.5],
      size: [2, 2],
      tones: [YELLOW, GOLD],
      alpha: [1, 0.6],
      layer: 'back',
    },
  ],
  gun: [
    // The match smouldering in its serpentine: an ember breathing, a wisp of smoke curling up off it.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 27, y: 10 },
      r: [2, 4],
      alpha: [0.25, 0.55],
      period: 0.7,
      tone: ORANGE,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 27, y: 9 },
      box: [1, 1],
      rate: 4,
      v: [-4, 2, -12, -8],
      g: -6,
      drag: 0.6,
      wobble: [1.5, 2],
      life: [0.5, 0.9],
      size: [1, 3],
      tones: [SILVER, GREY],
      alpha: [0.7, 0],
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 27, y: 10 },
      box: [1, 1],
      rate: 2,
      v: [-6, 6, -16, -8],
      g: 20,
      life: [0.15, 0.3],
      size: [1, 1],
      tones: [YELLOW, ORANGE],
      add: true,
      layer: 'gun',
    },
  ],
};

/** The Pirate set (ADR-0160 amended). */
export const PIRATE = drawnLater({ head: TRICORN, outfit: COAT, gun: BRASS, flair: FLAIR });
